import { axios } from "@/axios"
import { EventTemplate } from "@/models"

/**
 * Updates an existing custom event template.
 *
 * @param {EventTemplate} template - The template object containing updated details such as name, description, images, styles, and settings.
 * @throws {Error} If `template.id` is missing, as it is required for updating an existing template.
 * @returns {Promise<EventTemplate>} The updated event template.
 */
export async function updateCustomTemplate(template: EventTemplate) {
  const form = new FormData()

  if (template.name)
    form.append("Name", template.name)
  if (template.description)
    form.append("Description", template.description)
  if (template.headerImage)
    form.append("HeaderImage", template.headerImage)
  if (template.pathToHeaderImage)
    form.append("PathToHeaderImage", template.pathToHeaderImage)
  if (template.footerImage)
    form.append("FooterImage", template.footerImage)
  if (template.pathToFooterImage)
    form.append("PathToFooterImage", template.pathToFooterImage)
  if (template.additionalHostTerm?.key) {
    form.append("AdditionalHostTerm[Key]", template.additionalHostTerm.key)
  }
  if (template.additionalHostTerm?.value) {
    form.append("AdditionalHostTerm[Value]", template.additionalHostTerm.value)
  }
  if (template.optInSentence?.key) {
    form.append("OptInSentence[Key]", template.optInSentence.key)
  }
  if (template.optInSentence?.value) {
    form.append("OptInSentence[Value]", template.optInSentence.value)
  }
  if (template.themeJson) {
    form.append("ThemeJSON", JSON.stringify(template.themeJson))
  }

  form.append("RequireAdditionalHostTerms", template.requireAdditionalHostTerms ? "true" : "false")
  form.append("CollectGuestEmailsForMarketing", template.collectGuestEmailsForMarketing ? "true" : "false")

  const socialMediaLinks = template.customFooterLinks?.socialLinks

  if (socialMediaLinks?.facebook)
    form.append("TemplateSettings[CustomFooterLinks][SocialLinks][Facebook]", socialMediaLinks?.facebook)
  if (socialMediaLinks?.instagram)
    form.append("TemplateSettings[CustomFooterLinks][SocialLinks][Instagram]", socialMediaLinks?.instagram)
  if (socialMediaLinks?.youtube)
    form.append("TemplateSettings[CustomFooterLinks][SocialLinks][Youtube]", socialMediaLinks?.youtube)
  if (socialMediaLinks?.tiktok)
    form.append("TemplateSettings[CustomFooterLinks][SocialLinks][Tiktok]", socialMediaLinks?.tiktok)
  if (socialMediaLinks?.pinterest)
    form.append("TemplateSettings[CustomFooterLinks][SocialLinks][Pinterest]", socialMediaLinks?.pinterest)
  if (socialMediaLinks?.linkedIn)
    form.append("TemplateSettings[CustomFooterLinks][SocialLinks][LinkedIn]", socialMediaLinks?.linkedIn)
  if (socialMediaLinks?.x)
    form.append("TemplateSettings[CustomFooterLinks][SocialLinks][X]", socialMediaLinks?.x)

  const footerLinks = template.customFooterLinks?.footerLinks || []

  footerLinks.forEach((link, index) => {
    if (link.key)
      form.append(`TemplateSettings[CustomFooterLinks][FooterLinks][${index}][Key]`, link.key)
    if (link.value)
      form.append(`TemplateSettings[CustomFooterLinks][FooterLinks][${index}][Value]`, link.value)
  })

  const response = await axios.patch(`api/template/${template.id}`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  })

  return EventTemplate.fromResponse(response.data.Data)
}
